<script>
import { radioStreamQualities } from '@/configuration'
import DataLayer from '@services/data-layer/index'
import externalPlayerTracker from '@services/mixpanel/external-player-tracker'
import bannerTracker from '@services/mixpanel/banner-tracker'
import analyticsService from '@services/analytics-service'
import copyToClipboard from '@utils/copy-to-clipboard'
import messages from '@data/messages'

// Frozen: bannerTracker maps this to the SETTINGS_EXTERNAL_PLAYERS banner surface
// (analytics-spec.js) and the plans page reads it back off the `from` query. The
// upsell funnel only stays joined up while this exact string is untouched.
const UPSELL_SOURCE = 'settings-external-players'

// Quality and format are page-level choices — picked once, applied to every channel
// row — so the selector options are derived from the shared radio quality list.
const qualityOptionLabel = quality => `${quality.label} · ${quality.bitrate}`

export default {
    page() {
        const title = 'Settings - External Players'
        const description = 'Settings - External Players'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },

    data() {
        return {
            qualities: radioStreamQualities,
            formats: ['pls', 'm3u'],
            selectedQualityKey: 'mp3_high',
            selectedFormat: 'pls',
            stations: [],
            isLoading: false,
            loadFailed: false,
        }
    },

    computed: {
        memberId() {
            return this.$store.getters['member/currentMember'].id
        },
        isPremium() {
            return this.$store.getters['premium/isPremium']
        },
        hasEverBeenPremium() {
            return this.$store.getters['premium/hasEverBeenPremium']
        },
        // The same trial-eligibility rule the plans page runs on (view-plans.vue's
        // `entitledFreeTrial`, spent by subscription-tile.vue's `actionText`), so this
        // tab can never promise a trial the plans page won't go on to honour.
        ctaText() {
            return this.hasEverBeenPremium ? 'Go Premium' : messages.PREMIUM_UPSELL_CTA_FREE_TRIAL
        },
        qualityOptions() {
            return this.qualities.map(qualityOptionLabel)
        },
        activeQuality() {
            return this.qualities.find(quality => quality.key === this.selectedQualityKey)
        },
        activeQualityOption() {
            return this.activeQuality ? qualityOptionLabel(this.activeQuality) : ''
        },
        // Named on its own so a row with nothing to offer can point at the quality to
        // change away from, instead of just saying "unavailable" and stranding the member.
        activeQualityLabel() {
            return this.activeQuality ? this.activeQuality.label : ''
        },
    },

    mounted() {
        externalPlayerTracker.onPageViewed(this.isPremium)

        if (this.isPremium) {
            this._loadStreamUrls()
            return
        }

        // A free member has no stream urls to fetch — the api refuses the request — so
        // the tab pitches premium instead, and rendering that pitch is the impression.
        bannerTracker.onBannerSeen(UPSELL_SOURCE)
    },

    methods: {
        // Optional all the way down. `hasSelectedQuality` asks whether the quality is THERE,
        // not whether it is WHOLE, so a half-built payload reaches this read — and the read
        // happens inside the render, where a throw is not an exception the caller sees: Vue
        // abandons the render and keeps the previous vnode, so one malformed row blanks the
        // whole tab. A malformed row degrades to no url instead, which the template and
        // handleDownload both already know what to do with.
        playlistUrl(station) {
            const quality = this._selectedQuality(station)
            return quality?.playlists?.[this.selectedFormat] || ''
        },

        streamUrl(station) {
            const quality = this._selectedQuality(station)
            return quality?.url || ''
        },

        // A station's payload carries only the mounts it actually has, so the page-level
        // quality can be missing from any given row. Both of the row's controls hang off
        // this one predicate: an empty `href` is not an inert link — the html spec
        // resolves it against the current document, so clicking it would full-page-reload
        // the app out from under the player — and a Copy URL that silently does nothing
        // is no better. Neither is offered unless there is something behind it.
        hasSelectedQuality(station) {
            return Boolean(this._selectedQuality(station))
        },

        handleQualityChange(option) {
            const selected = this.qualities.find(quality => qualityOptionLabel(quality) === option)
            if (!selected) return

            this.selectedQualityKey = selected.key
        },

        // `_loadStreamUrls` clears `loadFailed` before re-fetching, so a retry is simply
        // another go at the same load.
        handleRetry() {
            this._loadStreamUrls()
        },

        // The <a href> does the navigating, and the api's Content-Disposition header is
        // what turns that navigation into a download — so there is nothing to do here
        // but count it. Never fetch a playlist url: those endpoints send no CORS headers.
        handleDownload(station) {
            // The template renders no anchor without a url, so this is belt-and-braces —
            // but it is what keeps the count honest for any other caller.
            if (!this.playlistUrl(station)) return

            externalPlayerTracker.onPlaylistDownloaded({
                channel: station.id,
                quality: this.selectedQualityKey,
                format: this.selectedFormat,
            })
        },

        // Copies the audio stream url (it carries `?token=`), never the playlist url
        // (`?key=`) — they are different endpoints, and pasting a playlist url into a
        // player's "open stream" box hands it a file where it expected a stream.
        handleCopyUrl(station) {
            const url = this.streamUrl(station)
            if (!url) return

            copyToClipboard(url)
            externalPlayerTracker.onUrlCopied({
                channel: station.id,
                quality: this.selectedQualityKey,
            })
            this._notify(this.$m('STREAM_URL_COPIED'), 'success')
        },

        // Two funnels, both of them fed: GA's upsell funnel — which every other upsell CTA in
        // the app reports to (premium-banner.vue, top-bar.vue, side-bar.vue, account/index.vue,
        // queue-service, app-initializer) — and Mixpanel's banner funnel. An entry point that
        // reports one but not the other under-counts silently, with nothing to show it is wrong.
        handleGoPremium() {
            analyticsService.premiumUpsellInitiated(UPSELL_SOURCE)
            bannerTracker.onBannerTapped(UPSELL_SOURCE)
            this.$router.push({ name: 'premium/view-plans', query: { from: UPSELL_SOURCE } })
        },

        _selectedQuality(station) {
            return (station.qualities || []).find(quality => quality.id === this.selectedQualityKey)
        },

        async _loadStreamUrls() {
            this.isLoading = true
            this.loadFailed = false

            try {
                const response = await DataLayer.streamUrls.getByMemberId(this.memberId)
                this.stations = response.body.stations
            } catch (error) {
                // A refused request rejects rather than resolving, and a 403 carries an
                // empty body — `error.status` is all there is, so there is nothing to parse.
                this.loadFailed = true
                console.error('Failed to load stream urls', error)
            } finally {
                this.isLoading = false
            }
        },

        _notify(message, type) {
            this.$store.dispatch('appearance/handleNotification', { message, type })
        },
    },
}
</script>

<template>
    <BaseSection padded>
        <template v-if="isPremium">
            <h6 :class="$style.subtitle">External Players</h6>
            <p :class="$style.intro">
                Play FRISKY radio channels on an external player like Winamp, VLC, or any hardware
                device that accepts pls or m3u files.
            </p>

            <div :class="$style.controls">
                <BaseCustomSelect
                    :class="$style.quality"
                    :options="qualityOptions"
                    :active="activeQualityOption"
                    prefix="Audio Quality"
                    @change="handleQualityChange"
                />
                <div
                    :class="$style.formats"
                    role="group"
                    aria-label="Playlist format"
                >
                    <button
                        v-for="format in formats"
                        :key="format"
                        :class="[$style.format, { [$style.formatActive]: selectedFormat === format }]"
                        :aria-pressed="selectedFormat === format ? 'true' : 'false'"
                        type="button"
                        @click="selectedFormat = format"
                    >.{{ format }}</button>
                </div>
            </div>

            <p
                v-if="isLoading"
                :class="$style.state"
            >Loading your stream links…</p>
            <p
                v-else-if="loadFailed"
                :class="$style.state"
            >
                We couldn't load your stream links.
                <button
                    :class="$style.action"
                    type="button"
                    @click="handleRetry"
                >Try again</button>
            </p>
            <ul
                v-else
                :class="$style.channels"
            >
                <li
                    v-for="station in stations"
                    :key="station.id"
                    :class="$style.channel"
                >
                    <span :class="$style.identity">
                        <span :class="[$style.dot, $style[station.id]]" />
                        <span :class="$style.channelTitle">{{ station.title }}</span>
                    </span>
                    <span :class="$style.actions">
                        <template v-if="hasSelectedQuality(station)">
                            <!-- A plain navigation, never a fetch: the playlist endpoints send no
                                 CORS headers, and their Content-Disposition header is what makes
                                 the browser save the file instead of opening it.

                                 The `v-if` is the last line of defence on the href: `hasSelectedQuality`
                                 vouches for the quality being there, not for it being whole, so a
                                 malformed payload can still leave `playlistUrl` with nothing to return.
                                 An `href=""` is NOT an inert link — the html spec resolves the empty
                                 string against the current document, so clicking one full-page-reloads
                                 the SPA out from under the player. No url, no anchor. -->
                            <a
                                v-if="playlistUrl(station)"
                                :class="$style.download"
                                :href="playlistUrl(station)"
                                @click="handleDownload(station)"
                            >Download</a>
                            <button
                                :class="$style.copy"
                                type="button"
                                @click="handleCopyUrl(station)"
                            >Copy URL</button>
                        </template>
                        <!-- Nothing to download and nothing to copy: say so, and name the quality
                             to change away from, rather than leave two controls that do nothing. -->
                        <span
                            v-else
                            :class="$style.unavailable"
                        >Not available in {{ activeQualityLabel }}</span>
                    </span>
                </li>
            </ul>
        </template>

        <!-- A free member has no stream links to show — the api refuses them — so the tab
             makes the case for the ones they could have. Nothing in this branch touches
             `stations`, reads a url, or renders a control that could hand one over. -->
        <template v-else>
            <div :class="$style.pitch">
                <h6 :class="[$style.subtitle, $style.pitchTitle]">Play FRISKY radio channels anywhere</h6>
                <p :class="$style.intro">
                    Play FRISKY radio channels in external players like Winamp, VLC, vTuner or any
                    player or hardware device that accepts direct pls or m3u links. Another way FRISKY
                    Premium is the best way to enjoy the best underground electronic music on the planet.
                </p>

                <!-- The premium banner's CTA, to the prop (premium-banner.vue): one Go Premium
                     button, one look, wherever a member meets it. Bought with `size` and
                     `button-type` rather than a css override, because the two compile to sibling
                     compound selectors of EQUAL specificity — a call-site override would be a
                     source-order coin toss.

                     `@click`, never `@click.native`: BaseButton re-emits its click via
                     `v-on="$listeners"`, and an empty `$listeners` is what its `isLabel`
                     computed reads to apply `cannotBeHovered` — `pointer-events: none`.
                     A `.native` binding here would compile to a CTA nothing can click. -->
                <!-- The wrapper, not the button, owns the mobile centering: BaseButton's
                     `noMargins`/`autoWidth` compound selectors would out-rank (or coin-toss)
                     a call-site margin override, but a flex wrapper never competes with them. -->
                <div :class="$style.pitchCta">
                    <BaseButton
                        :modifiers="{ noMargins: true, autoWidth: true }"
                        button-type="primaryFlat"
                        size="hero"
                        @click="handleGoPremium"
                    >{{ ctaText }}</BaseButton>
                </div>
            </div>
        </template>
    </BaseSection>
</template>

<style lang="scss" module>
@use '@design' as *;

// The channel row's two filled controls (Figma 860:33). Deliberately NOT BaseButtons:
// BaseButton renders a <button>, and Download has to stay a plain <a href> — the playlist
// endpoints send no CORS headers, so only an anchor navigation lets the api's
// Content-Disposition header save the file; a button would have nothing to click to.
// So the pair borrows BaseButton's tokens instead of the component — its radius, its 600
// weight, and the exact hover targets of its `highlight` and `white` types — reached with
// a plain background swap, the treatment BaseButton's own `.secondaryPink` and `.nadir`
// types use rather than its ::before/::after cross-fade. One mixin, so an anchor and a
// button that must look identical cannot drift apart.
@mixin rowButton {
    @extend %contentSubtitleM;

    display: flex;
    align-items: center;
    justify-content: center;
    // Sized to the row rather than to the design system: one-off literals, like
    // `.quality`'s width below, that no $size-* token elsewhere would ever consume. The
    // min-width is what holds the two buttons to an even, matched pair — narrower on a
    // phone, where every pixel it takes is one the channel name gives up, but never
    // narrower than "Download" sets, or it stops binding and the pair goes uneven.
    min-width: 96px;
    height: 40px;
    padding: 0 $s-s;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    border: 0;
    border-radius: $button-border-radius;
    transition: background 0.3s ease-in-out, transform 0.1s ease-in-out;

    // The press every other button in the app has (_base-button.vue's `.button`).
    &:active {
        transform: scale(0.95);
    }

    @include tablet {
        min-width: 104px;
        padding: 0 $s-m;
    }
}

.subtitle {
    margin-top: 0;
    margin-bottom: $s-s;
    text-align: center;

    @include tablet {
        text-align: left;
    }
}

.intro {
    @extend %contentSubtitleM;

    margin: 0 0 $s-xl;
    color: $c-text-faded;
}

.controls {
    display: flex;
    flex-direction: column;
    gap: $s-s;
    align-items: stretch;
    margin-bottom: $s-xl;

    @include tablet {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

// Wide enough for the "Audio Quality" label and the longest option ("HI-FI · 320 kbps")
// side by side, so that the select — and the format toggle beside it — stay put as the
// selection changes, and the value never folds onto a second line. A one-off layout width
// with no design-system meaning: kept a literal rather than promoted to a global $size-*
// token nothing else would ever consume.
.quality {
    @include tablet {
        width: 260px;
    }
}

// A two-option segmented toggle rather than a dropdown — the whole page follows it,
// so both formats stay visible and one tap away.
.formats {
    display: flex;
    align-items: stretch;
    border: 1px solid $c-shark;
    border-radius: $size-radius-m;
}

.format {
    @extend %contentSubtitleM;

    flex: 1;
    padding: $s-xs $s-l;
    color: $c-text-faded;
    cursor: pointer;
    background: none;
    border: 0;
    border-radius: $size-radius-m;

    &:hover {
        color: $c-white;
    }
}

.formatActive {
    color: $c-white;
    background: $c-highlight;
}

.state {
    @extend %contentSubtitleM;

    margin: 0;
    color: $c-text-faded;
}

.channels {
    padding: 0;
    margin: 0;
    list-style: none;
}

// A row at every width, phone included: the actions belong beside the channel they act
// on, not stacked under it. It is a tight fit on a narrow phone — two buttons and a name
// — so the actions hold their size and the name gives way (see .identity below).
.channel {
    display: flex;
    gap: $s-xs;
    align-items: center;
    justify-content: space-between;
    padding: $s-m $s-s;
    background: $c-steel-grey;
    border-radius: $size-radius-m;

    ~ .channel {
        margin-top: $s-xs;
    }

    // Alternating fills, so the four stacked cards read as four rows and not one slab.
    // The rows used to be a flat $c-alt-bg, which was too dark to tell apart; that darker
    // shade now does the contrast job on every second row instead.
    &:nth-child(even) {
        background: $c-alt-bg;
    }

    @include tablet {
        padding: $s-m $s-l;
    }
}

// `min-width: 0` is what lets the title truncate instead of shouldering the buttons off
// the row: a flex item will not shrink below its content unless told it may.
.identity {
    display: flex;
    flex: 1;
    gap: $s-xs;
    align-items: center;
    min-width: 0;

    @include tablet {
        gap: $s-s;
    }
}

// The channel's gradient carries its identity — the row is otherwise deliberately plain.
.dot {
    @include square($s-m);

    flex-shrink: 0;
    border-radius: 50%;
}

.frisky {
    background: $c-frisky-channel-grad;
}

.deep {
    background: $c-deep-channel-grad;
}

.chill {
    background: $c-chill-channel-grad;
}

.classics {
    background: $c-classics-channel-grad;
}

// Bold, and set outright rather than reached for from a mixin: h4 is 24px but weight 900,
// h5 only reaches 24px on desktop — neither is a fixed size at 700.
//
// The design says 24px, but its row is 96px tall where ours is half that, so 24px reads
// far heavier here than it does there; 18px carries the row without swamping it. The phone
// gets 16px because the buttons now sit beside the name and there is not the room: at 24px
// "Classics" needed 116px of the ~70px left over.
.channelTitle {
    overflow: hidden;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: $c-white;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include tablet {
        font-size: 18px;
        line-height: 26px;
    }
}

// Never squashed: the pair keeps its size and the channel title truncates instead. The
// gap tightens on a phone, where the two buttons and the name are competing for ~320px.
.actions {
    display: flex;
    flex-shrink: 0;
    gap: $s-xs;
    align-items: center;

    @include tablet {
        gap: $s-l;
    }
}

// The page's one inline text control: "Try again", set inside the failed-load sentence,
// where a filled button would read as a CTA competing with the rows it is standing in for.
// (Download and Copy URL were styled from here too, until they became real buttons.)
.action {
    @extend %contentSubtitleS;

    padding: 0;
    color: $c-highlight;
    text-decoration: none;
    cursor: pointer;
    background: none;
    border: 0;

    &:hover {
        color: $c-white;
    }
}

// The row's filled pair — pink Download, white Copy URL — with the hover targets lifted
// off BaseButton's `highlight` and `white` types so they read as the same buttons.
.download {
    @include rowButton;

    color: $c-white;
    background: $c-highlight;

    &:hover {
        background: lighten($c-highlight, 10%);
    }
}

.copy {
    @include rowButton;

    color: $c-black;
    background: $c-white;

    &:hover {
        background: darken($c-white, 10%);
    }
}

// The quiet counterpart to `.action`: a row with no url in the selected quality states
// that plainly, in the faded body colour, where its two controls would have been.
// (%contentSubtitleS already carries $c-text-faded.)
.unavailable {
    @extend %contentSubtitleS;
}

// FREE STATE — the premium pitch: one column of the argument, the players it opens FRISKY
// up to, and the CTA.
//
// Constrained rather than left to fill the pane. The settings pane is `width: 100%` of the
// main column, which runs past 800px on a wide desktop — enough to strand this 12px copy in
// one edge-to-edge line. A one-off reading measure with no design-system meaning: kept a
// literal, like `.quality` above, rather than promoted to a $size-* token nothing else
// would ever consume.
.pitch {
    max-width: 560px;
}

// The tab's only heading for a free member, so it takes the same slot in the outline as
// the premium branch's "External Players" (hence the shared `.subtitle`) and one step up
// the type scale — this is the whole page, not a section of it.
.pitchTitle {
    @include h5;

    margin-bottom: $s-s;
}

// On a phone the pitch title centers itself over the copy, so a left-flush CTA reads
// as misaligned; centre it under the text. Desktop keeps the left-aligned Figma layout.
.pitchCta {
    @include mobileOnly {
        display: flex;
        justify-content: center;
    }
}

</style>
